/*
 * ==========================================================================
 * SingRank - Meta Pixel ENTRY capture for Ablink's inventory pages   v1.0.1
 * --------------------------------------------------------------------------
 * WHY THIS EXISTS
 * singrank-meta-tracker.js captures INTENT: a click on a vehicle card, a
 * WhatsApp hand-off, a COE calculation, a search. But a visitor who arrives
 * from Google, scrolls the inventory and leaves without clicking anything
 * sent Meta NOTHING about vehicles at all - only the Shopify app pixel's
 * empty PageView. That visitor could not be retargeted, which is the whole
 * point of the campaign.
 *
 * The gap is precise: the tracker's fireFromUrl() only fires when the URL
 * carries "v=". Landing on a bare /pages/car-marketplace fires nothing.
 *
 * This file closes it, on these two pages only:
 *   /pages/new-commercial-vehicles    52 new    cards .abl-card
 *   /pages/car-marketplace           132 used   cards .t8-card
 * (/pages/coe-renewal-... and /pages/motor-insurance already fire on entry
 * via the tracker's own servicePageIntent, so they are deliberately skipped.)
 *
 * THREE TIERS OF INTENT, kept as separate events so the ads team can weight
 * them differently instead of guessing:
 *   VehicleListView    trackCustom   landed on an inventory page. Carries the
 *                                    ids actually on screen, the powertrain
 *                                    mix and the active filter. Re-fires when
 *                                    the visitor changes the filter.
 *   VehicleImpression  trackCustom   THIS vehicle was genuinely on screen for
 *                                    at least DWELL_MS. Not a click, but far
 *                                    stronger than a page view.
 *   VehicleView        trackCustom   a real click - still owned by the tracker,
 *                                    never fired here, so clicks stay clean.
 *
 * IT INVENTS NOTHING. Every payload is produced by the tracker's own reader,
 * window.SR_META_TRACKER.read(card), so the taxonomy, the used-vehicle id
 * lookup and the "never guess a fuel" rule are the proven ones. If the
 * tracker is absent this file does nothing at all.
 *
 * SAFETY
 * - Renders nothing. Touches no layout, content, styling or existing handler.
 * - Listeners are passive; it never calls preventDefault.
 * - Every path is wrapped in try/catch; on failure the page is unaffected.
 * - De-duplicated per subject per page view, and capped, so a long scroll
 *   cannot flood the pixel.
 * - No regular expressions and no escape sequences, by house rule.
 *
 * DEBUG
 *   window.SR_META_ENTRY.fired    everything this file sent
 *   window.SR_META_ENTRY.peek()   read the entry payload WITHOUT sending it
 *
 * v1.0.1 - impressions were silently dead after any filter change. Both
 * pages REBUILD the card list rather than hiding cards (52 become 8), so
 * the observer was left holding detached nodes. Found in a real browser,
 * not by reading the code. It now re-observes after a filter and watches
 * the grid for its own mutations.
 *
 * Code by SingRank (https://singrank.com) for AB Link Pte Ltd. Created 2026-08-26.
 * ==========================================================================
 */
(function () {
  'use strict';

  if (window.SR_META_ENTRY) { return; }

  var API = { version: '1.0.1', fired: [], peek: null };
  window.SR_META_ENTRY = API;

  var FIRED = {};

  // Cards render only after the backend answers, and the used-vehicle reader
  // needs the tracker's /api/pricelist index to be in place before it can tell
  // a HYBRID from an Unknown. Both are polled rather than assumed.
  var POLL_MS = 500;
  var MAX_POLLS = 60;
  var MAX_FBQ_TRIES = 40;

  // A Meta payload is not the place for 132 ids. The list event carries a
  // sample; VehicleImpression covers what the visitor actually looked at.
  var MAX_LIST_IDS = 30;

  // Long enough that scrolling straight past a card does not count as looking
  // at it, short enough that a genuine pause does.
  var DWELL_MS = 1500;
  var MAX_IMPRESSIONS = 12;

  var REFILTER_SETTLE_MS = 900;

  var impressionsSent = 0;

  function tracker() {
    var t = window.SR_META_TRACKER;
    return (t && typeof t.read === 'function') ? t : null;
  }

  function pageKind() {
    try {
      var p = window.location.pathname;
      if (p.indexOf('car-marketplace') > -1) { return 'used'; }
      if (p.indexOf('new-commercial-vehicles') > -1) { return 'new'; }
      return '';
    } catch (e) { return ''; }
  }

  function cards() {
    try { return document.querySelectorAll('.abl-card, .t8-card'); }
    catch (e) { return []; }
  }

  function visibleCards() {
    var all = cards();
    var out = [];
    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      try {
        // Measured 2026-08-26: a filter REBUILDS this list, so most filtered
        // cards are gone from the DOM rather than hidden. This check is the
        // safety net for any that are merely hidden - a card nobody can see
        // must never count towards the audience either way.
        if (el.offsetParent === null) { continue; }
      } catch (e) {}
      out.push(el);
    }
    return out;
  }

  function fire(eventName, data, dedupeKey) {
    try {
      if (!data) { return; }
      if (FIRED[dedupeKey]) { return; }
      FIRED[dedupeKey] = true;

      API.fired.push({ event: eventName, data: data });

      var tries = 0;
      (function attempt() {
        if (typeof window.fbq !== 'function') {
          tries = tries + 1;
          if (tries > MAX_FBQ_TRIES) { return; }
          window.setTimeout(attempt, 250);
          return;
        }
        // Everything here is ours, so trackCustom only. Nothing this file
        // sends can collide with or inflate a Shopify app-pixel event.
        try { window.fbq('trackCustom', eventName, data); } catch (e) {}
      })();
    } catch (e) {}
  }

  /* ---------- the entry / list event ---------- */

  function buildListPayload() {
    var t = tracker();
    if (!t) { return null; }

    var kind = pageKind();
    if (!kind) { return null; }

    var list = visibleCards();
    if (!list.length) { return null; }

    var ids = [];
    var mix = {};
    var counted = 0;
    var priced = 0;
    var lowest = 0;

    for (var i = 0; i < list.length; i++) {
      var d = null;
      try { d = t.read(list[i]); } catch (e) { d = null; }
      if (!d || !d.content_name) { continue; }
      counted = counted + 1;

      if (ids.length < MAX_LIST_IDS && d.content_ids && d.content_ids[0]) {
        ids.push(String(d.content_ids[0]));
      }

      var p = d.powertrain || 'Unknown';
      mix[p] = (mix[p] || 0) + 1;

      if (d.value) {
        priced = priced + 1;
        if (!lowest || d.value < lowest) { lowest = d.value; }
      }
    }

    if (!counted) { return null; }

    var parts = [];
    for (var k in mix) {
      if (mix.hasOwnProperty(k)) { parts.push(k + ' ' + mix[k]); }
    }
    parts.sort();

    var state = null;
    try {
      if (typeof t.readSearch === 'function') { state = t.readSearch(); }
    } catch (e) { state = null; }

    var filters = (state && state.filters) ? state.filters.join(' | ') : '';
    var typed = (state && state.search_string) ? state.search_string : '';
    var category = (state && state.content_category)
      ? state.content_category
      : (kind === 'used' ? 'All Used Vehicles' : 'All New Commercial Vehicles');

    return {
      content_ids:       ids,
      content_type:      'vehicle',
      content_name:      category,
      content_category:  category,
      vehicles_shown:    counted,
      vehicles_priced:   priced,
      powertrain_mix:    parts.join(' | '),
      filters:           filters,
      search_string:     typed,
      vehicle_condition: kind === 'used' ? 'Used' : 'New',
      source_page:       kind === 'used' ? 'car-marketplace' : 'new-commercial-vehicles',
      value:             lowest,
      currency:          'SGD'
    };
  }

  API.peek = buildListPayload;

  function sendList() {
    var d = buildListPayload();
    if (!d) { return false; }
    // The filter state is in the key, so refining the list is a NEW audience
    // signal rather than a duplicate that gets swallowed.
    var key = 'list::' + d.content_category + '::' + d.filters + '::'
            + d.search_string + '::' + d.vehicles_shown;
    fire('VehicleListView', d, key);
    return true;
  }

  var refilterTimer = null;

  function queueRefilter() {
    if (refilterTimer) { window.clearTimeout(refilterTimer); }
    refilterTimer = window.setTimeout(function () {
      try {
        sendList();
        // The card nodes have just been replaced, so re-arm the viewport
        // watcher against the new ones or impressions stop for good.
        observeCards();
      } catch (e) {}
    }, REFILTER_SETTLE_MS);
  }

  /* ---------- per-vehicle impressions ---------- */

  function sendImpression(card) {
    try {
      if (impressionsSent >= MAX_IMPRESSIONS) { return; }
      var t = tracker();
      if (!t) { return; }

      var d = null;
      try { d = t.read(card); } catch (e) { d = null; }
      if (!d || !d.content_name) { return; }

      var id = (d.content_ids && d.content_ids[0]) ? String(d.content_ids[0]) : d.content_name;
      var key = 'impr::' + id;
      if (FIRED[key]) { return; }

      d.engagement = 'viewport';
      impressionsSent = impressionsSent + 1;
      fire('VehicleImpression', d, key);
    } catch (e) {}
  }

  // BOTH inventory pages REBUILD their card list when a filter changes - the
  // old nodes are removed from the DOM, not hidden. Measured on the live page:
  // 52 cards become 8. So observing once at start-up silently stops working
  // the moment a visitor touches a filter, because every observed node is
  // detached and the replacements were never observed. Verified in a real
  // browser 2026-08-26; it produced zero impressions until this was fixed.
  // The observer is therefore kept and RE-APPLIED, both after a filter change
  // and whenever the grid mutates on its own.
  var cardObserver = null;
  var dwellTimers = null;
  var reobserveTimer = null;

  function onIntersect(entries) {
    for (var i = 0; i < entries.length; i++) {
      var e = entries[i];
      var el = e.target;
      if (e.isIntersecting) {
        if (dwellTimers.get(el)) { continue; }
        dwellTimers.set(el, window.setTimeout(function (node) {
          return function () {
            dwellTimers.set(node, null);
            sendImpression(node);
            try { cardObserver.unobserve(node); } catch (err) {}
          };
        }(el), DWELL_MS));
      } else {
        var handle = dwellTimers.get(el);
        if (handle) { window.clearTimeout(handle); dwellTimers.set(el, null); }
      }
    }
  }

  function observeCards() {
    try {
      if (!window.IntersectionObserver || !window.WeakMap) { return; }
      if (!dwellTimers) { dwellTimers = new window.WeakMap(); }
      if (!cardObserver) {
        cardObserver = new window.IntersectionObserver(onIntersect, { threshold: 0.5 });
      }
      // observe() on an element already being watched is a no-op, so calling
      // this repeatedly is safe and needs no bookkeeping of its own.
      var list = cards();
      for (var n = 0; n < list.length; n++) { cardObserver.observe(list[n]); }
    } catch (e) {}
  }

  function queueReobserve() {
    if (reobserveTimer) { window.clearTimeout(reobserveTimer); }
    reobserveTimer = window.setTimeout(observeCards, 400);
  }

  // A filter is not the only thing that swaps cards - both pages also render
  // asynchronously as their backend answers. Watching the grid itself means we
  // never have to guess which interactions rebuild it.
  function watchGrid() {
    try {
      if (!window.MutationObserver) { return; }
      var first = cards()[0];
      var grid = first ? first.parentElement : null;
      if (!grid) { return; }
      new window.MutationObserver(function () { queueReobserve(); })
        .observe(grid, { childList: true });
    } catch (e) {}
  }

  function watchViewport() {
    observeCards();
    watchGrid();
  }

  /* ---------- start ---------- */

  if (!pageKind()) { return; }

  var polls = 0;

  (function waitForReady() {
    try {
      var t = tracker();
      var ready = t && cards().length > 0;

      // On the used page the reader is only fully truthful once the tracker's
      // pricelist index has landed, so give it a few extra polls before the
      // one-shot list event. It still fires either way rather than being lost.
      if (ready && pageKind() === 'used' && polls < 8) {
        var probe = null;
        try { probe = t.read(cards()[0]); } catch (e) { probe = null; }
        if (probe && probe.powertrain === 'Unknown') { ready = false; }
      }

      if (ready) {
        sendList();
        watchViewport();

        document.addEventListener('click', function (ev) {
          try {
            if (ev.target && ev.target.closest && ev.target.closest('.abl-chip-btn')) {
              queueRefilter();
            }
          } catch (e) {}
        }, true);

        document.addEventListener('change', function (ev) {
          try {
            var el = ev.target;
            if (!el) { return; }
            var cls = String(el.className || '');
            var id = String(el.id || '');
            if (cls.indexOf('t8-select') > -1 || id.indexOf('abl-f-') === 0) {
              queueRefilter();
            }
          } catch (e) {}
        }, true);

        document.addEventListener('input', function (ev) {
          try {
            var el = ev.target;
            if (!el) { return; }
            var cls = String(el.className || '');
            var id = String(el.id || '');
            if (cls.indexOf('t8-search') > -1 || id.indexOf('abl-f-search') === 0) {
              queueRefilter();
            }
          } catch (e) {}
        }, true);

        return;
      }

      polls = polls + 1;
      if (polls > MAX_POLLS) { return; }
      window.setTimeout(waitForReady, POLL_MS);
    } catch (e) {}
  })();
})();
